import type { ErrorService } from '#src/telemetry/errors/error_service.ts';
import { exists } from '@packages/primitives/exists';

/** Returns unvalidated JSON. Empty successful bodies are rejected. Consumers parse their own contracts. */
export function createHttpClient({
  baseUrl,
  fetch: fetchRequest = globalThis.fetch,
  errorService,
}: HttpClientOptions): HttpClient {
  return {
    get: (path: string, options: RequestOptions = {}): Promise<unknown> =>
      request('GET', path, options),
    post: (path: string, body: unknown, options: RequestOptions = {}): Promise<unknown> =>
      request('POST', path, options, body),
    delete: (path: string, options: RequestOptions = {}): Promise<unknown> =>
      request('DELETE', path, options),
  };

  async function request(
    method: string,
    path: string,
    { query, headers, signal }: RequestOptions,
    body?: unknown,
  ): Promise<unknown> {
    signal?.throwIfAborted();
    const url = buildUrl(baseUrl, path, query);
    const requestHeaders = new Headers(headers);
    if (!requestHeaders.has('Content-Type')) {
      requestHeaders.set('Content-Type', 'application/json');
    }
    const serializedBody = JSON.stringify(body);
    signal?.throwIfAborted();

    let response: Response;
    try {
      response = await fetchRequest(url, {
        method,
        headers: requestHeaders,
        signal,
        ...(exists(serializedBody) && { body: serializedBody }),
      });
    } catch (error) {
      rethrowCancellation(error, signal);
      const failure = new HttpError('network');
      report(failure);
      throw failure;
    }

    let payload: unknown;
    try {
      signal?.throwIfAborted();
      payload = await response.json();
      signal?.throwIfAborted();
    } catch (error) {
      rethrowCancellation(error, signal);
      if (!response.ok) {
        throw new HttpError('http', response.status);
      }
      // JSON syntax failures differ from connection failures while consuming the body.
      const kind = error instanceof SyntaxError ? 'invalid-json' : 'network';
      const failure = new HttpError(kind, response.status);
      report(failure);
      throw failure;
    }

    if (!response.ok) {
      throw new HttpError('http', response.status, payload);
    }
    return payload;
  }

  function report(error: HttpError): void {
    // Native errors can contain response bodies or URLs. Report only fixed messages and status.
    try {
      const reporting = errorService?.captureError(new Error(error.message), {
        message: error.message,
        extra: { kind: error.kind, status: error.status },
      });
      // Also contain rejections from asynchronous implementations of the reporting contract.
      void Promise.resolve(reporting).catch(() => {});
    } catch {
      // Reporting must never replace the original transport failure.
    }
  }
}

export class HttpError extends Error {
  override readonly name = 'HttpError';

  constructor(
    readonly kind: 'http' | 'network' | 'invalid-json',
    readonly status?: number,
    readonly payload?: unknown,
  ) {
    super(
      kind === 'network'
        ? 'HTTP request failed'
        : kind === 'invalid-json'
          ? 'Received invalid JSON from the API'
          : 'Received an unsuccessful HTTP response',
    );
  }
}

export function isHttpError(error: unknown): error is HttpError {
  return error instanceof HttpError;
}

export interface HttpClient {
  get: (path: string, options?: RequestOptions) => Promise<unknown>;
  post: (path: string, body: unknown, options?: RequestOptions) => Promise<unknown>;
  delete: (path: string, options?: RequestOptions) => Promise<unknown>;
}

export interface HttpClientOptions {
  /** An HTTP(S) URL or a path such as /api. Request paths are appended to it. */
  baseUrl: string;
  fetch?: typeof globalThis.fetch;
  errorService?: Pick<ErrorService, 'captureError'>;
}

export interface RequestOptions {
  query?: Record<string, string>;
  headers?: HeadersInit;
  signal?: AbortSignal;
}

function rethrowCancellation(error: unknown, signal?: AbortSignal): void {
  signal?.throwIfAborted();
  if ((error instanceof Error || error instanceof DOMException) && error.name === 'AbortError') {
    throw error;
  }
}

function buildUrl(baseUrl: string, path: string, query?: Record<string, string>): string {
  const absolute = /^https?:\/\//i.test(baseUrl);
  // Parse the base and endpoint separately so a base query cannot swallow the endpoint path.
  const origin = 'https://fetching.invalid';
  const url = new URL(absolute ? baseUrl : `/${baseUrl.replace(/^\/+/, '')}`, origin);
  const endpoint = new URL(`/${path.replace(/^\/+/, '')}`, origin);
  url.pathname =
    `${url.pathname}/${endpoint.pathname}`.replace(/\/{2,}/g, '/').replace(/\/+$/, '') || '/';
  url.search = [url.search.slice(1), endpoint.search.slice(1)].filter(Boolean).join('&');
  url.hash = endpoint.hash;
  for (const [key, value] of Object.entries(query ?? {})) {
    url.searchParams.set(key, value);
  }
  return absolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
}
